/**
 * Popup runtime — decides *when* a configured popup fires.
 *
 * Kept separate from `main.ts` (which owns the launcher, teaser and chat
 * panel) because the trigger logic is where the interaction rules live:
 * one popup at a time, never over an open chat panel, never twice for the
 * same visitor.
 *
 * Conditions themselves are evaluated by `@prochat/shared` (pure, shared
 * with the API); this module only handles the parts that need a browser:
 * timers, scroll position, mouse intent, and per-visitor frequency state.
 */

import { mountPopup, type PopupController, type PopupSubmitValues } from '@prochat/widget-bridge'
import { evaluatePopupConditions, triggerDelayMs, type PopupContext } from '@prochat/shared'
import type { PopupPublicDto } from '@prochat/shared'
import {
  fetchPopupConfig,
  submitPopup,
  trackPopupEvent,
  trackPopupEventBeacon,
  type PopupPageContext,
} from './popup-client'
import { PopupFrequencyTracker, resolveAnonymousId } from './popup-frequency'

export interface PopupRunnerOptions {
  host: ShadowRoot | HTMLElement
  appKey: string
  apiBaseUrl: string
  /** Gathered from the page once; reused for every condition check. */
  page: PopupPageContext & { referrer?: string }
  /** True while the chat panel is open — popups must stay out of the way. */
  isChatOpen(): boolean
  /** Open the chat panel, optionally on the conversation a submit created. */
  onOpenChat?(conversationId?: string): void
  fetchImpl?: typeof fetch
  now?: () => number
}

export interface PopupRunner {
  /** Popups that passed every check but were withheld. For diagnostics. */
  suppressed: Array<{ popupId: string; reason: string }>
  destroy(): void
}

/** Default scrolling rule: how deep the visitor must be for `scrollPct`. */
function scrollPct(): number {
  const doc = document.documentElement
  const height = doc.scrollHeight - window.innerHeight
  if (height <= 0) return 100
  return Math.min(100, Math.round((window.scrollY / height) * 100))
}

function popupContext(page: PopupRunnerOptions['page'], now: Date): PopupContext {
  let hostname = ''
  let path = ''
  try {
    const parsed = new URL(page.url)
    hostname = parsed.hostname
    path = parsed.pathname
  } catch {
    // A malformed page URL should not stop a popup whose rules are empty.
  }
  return {
    url: page.url,
    path,
    hostname,
    referrer: page.referrer,
    device: page.device,
    utmSource: page.utmSource,
    utmMedium: page.utmMedium,
    utmCampaign: page.utmCampaign,
    now,
  }
}

/**
 * Start the popup system. Resolves once every popup has been armed; the
 * popups themselves appear later, from their trigger.
 *
 * Never rejects: a site with a broken popup endpoint must still get a
 * working chat widget.
 */
export async function startPopupRunner(opts: PopupRunnerOptions): Promise<PopupRunner> {
  const now = opts.now ?? (() => Date.now())
  const frequency = new PopupFrequencyTracker()
  // Resolved before the first call so the anonymous id travels with the
  // config request too — the server records impressions against it.
  const anonymousId = resolveAnonymousId(opts.appKey)
  const suppressed: Array<{ popupId: string; reason: string }> = []
  const controllers = new Map<string, PopupController>()
  const teardown: Array<() => void> = []
  let destroyed = false
  // One overlay at a time — two popups stacked on a phone is a modal
  // prison, and the visitor cannot dismiss the one underneath.
  let active: string | null = null

  function dismissActive(reason: 'close' | 'hidden'): void {
    const id = active
    if (!id) return
    active = null
    controllers.get(id)?.hide()
    if (reason === 'close') {
      void trackPopupEvent({
        apiBaseUrl: opts.apiBaseUrl,
        appKey: opts.appKey,
        popupId: id,
        event: 'close',
        anonymousId,
        fetchImpl: opts.fetchImpl,
      })
    }
  }

  function arm(popup: PopupPublicDto): void {
    if (destroyed) return
    const conditions = popup.conditions

    const present = () => {
      if (destroyed || active) return
      // The chat panel owns the same corner of the screen; a popup on top
      // of an open conversation reads as a bug to the visitor.
      if (opts.isChatOpen()) {
        // Re-check later rather than dropping the impression: the visitor
        // may close the panel without leaving the page.
        const timer = window.setTimeout(() => {
          if (!destroyed && !active) present()
        }, 1_000)
        teardown.push(() => window.clearTimeout(timer))
        return
      }
      if (!frequency.allows(popup.id, conditions?.frequency, now())) {
        suppressed.push({ popupId: popup.id, reason: 'frequency' })
        return
      }
      frequency.record(popup.id, now())
      active = popup.id
      const controller = controllers.get(popup.id) ?? mountPopup(opts.host, {
        popup,
        onSubmit: (values) => submitAndThank(popup, values),
        onOpenChat: () => {
          dismissActive('hidden')
          opts.onOpenChat?.()
        },
        onOpenUrl: (url) => {
          dismissActive('hidden')
          window.open(url, '_blank', 'noopener,noreferrer')
        },
        onClose: () => dismissActive('close'),
      })
      controllers.set(popup.id, controller)
      controller.show()
      void trackPopupEvent({
        apiBaseUrl: opts.apiBaseUrl,
        appKey: opts.appKey,
        popupId: popup.id,
        event: 'impression',
        anonymousId,
        fetchImpl: opts.fetchImpl,
      })
    }

    switch (conditions?.trigger) {
      case 'immediate': {
        const timer = window.setTimeout(present, 0)
        teardown.push(() => window.clearTimeout(timer))
        break
      }
      case 'scroll': {
        const wanted = conditions.scrollPct ?? 50
        const onScroll = () => {
          if (scrollPct() < wanted) return
          present()
          cleanup()
        }
        const cleanup = () => {
          window.removeEventListener('scroll', onScroll)
          // A page shorter than the threshold never reaches it.
          if (scrollPct() >= wanted) window.setTimeout(present, 0)
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        teardown.push(() => window.removeEventListener('scroll', onScroll))
        if (scrollPct() >= wanted) cleanup()
        break
      }
      case 'exit_intent': {
        // Only a real departure upward counts — `mouseleave` fires for any
        // boundary crossing, including moves toward a side edge.
        const onLeave = (event: MouseEvent) => {
          if (event.clientY > 0) return
          present()
          cleanup()
        }
        const cleanup = () => window.removeEventListener('mouseleave', onLeave)
        document.addEventListener('mouseleave', onLeave)
        teardown.push(() => document.removeEventListener('mouseleave', onLeave))
        break
      }
      case 'idle':
      case 'delay':
      default: {
        const ms = triggerDelayMs(conditions) ?? 0
        const timer = window.setTimeout(present, ms)
        teardown.push(() => window.clearTimeout(timer))
        break
      }
    }
  }

  async function submitAndThank(
    popup: PopupPublicDto,
    values: PopupSubmitValues,
  ): Promise<void> {
    const result = await submitPopup({
      apiBaseUrl: opts.apiBaseUrl,
      appKey: opts.appKey,
      popupId: popup.id,
      anonymousId,
      values,
      page: opts.page,
      fetchImpl: opts.fetchImpl,
    })
    if (!result.ok) throw new Error(result.error ?? 'submit failed')
    void trackPopupEvent({
      apiBaseUrl: opts.apiBaseUrl,
      appKey: opts.appKey,
      popupId: popup.id,
      event: 'submit',
      anonymousId,
      payload: { fields: Object.keys(values) },
      fetchImpl: opts.fetchImpl,
    })
    // The visitor just told us they want to talk — for some popups that is
    // the whole point, so hand off rather than leaving them on a "thanks".
    if (popup.postSubmit?.openChat) opts.onOpenChat?.(result.conversationId)
  }

  // A tab closed while a popup was up is a close, and it is the one close
  // `fetch` will never deliver — the request is cancelled by the unload.
  const onPageHide = () => {
    if (!active) return
    trackPopupEventBeacon({
      apiBaseUrl: opts.apiBaseUrl,
      appKey: opts.appKey,
      popupId: active,
      event: 'close',
      anonymousId,
    })
  }
  window.addEventListener('pagehide', onPageHide)
  teardown.push(() => window.removeEventListener('pagehide', onPageHide))

  try {
    const config = await fetchPopupConfig({
      apiBaseUrl: opts.apiBaseUrl,
      appKey: opts.appKey,
      url: opts.page.url,
      anonymousId,
      fetchImpl: opts.fetchImpl,
    })
    if (destroyed) return runner()

    // Highest priority first, so a "free consultation" popup outranks a
    // generic newsletter one on a page where both qualify.
    const ordered = [...config].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    for (const popup of ordered) {
      const verdict = evaluatePopupConditions(
        popup.conditions,
        popupContext(opts.page, new Date(now())),
      )
      if (!verdict.show) {
        suppressed.push({ popupId: popup.id, reason: verdict.reason })
        continue
      }
      arm(popup)
    }
  } catch {
    // No config, bad gateway, blocked domain — the chat widget still works.
  }

  function runner(): PopupRunner {
    return {
      suppressed,
      destroy() {
        destroyed = true
        for (const fn of teardown) fn()
        for (const controller of controllers.values()) controller.destroy()
        controllers.clear()
      },
    }
  }

  return runner()
}
